<template>
  <AdminLayout page="Emergency Contacts" title="Emergency Contacts">
    <Loading v-if="isPropertyLoading" />
    <div v-else-if="userHasPermission">
      <BackButton :to="`/${bldgStore.siteCode}/admin`"> Admin Home </BackButton>
      <div v-if="numbers && numbers.length > 0" class="mt-8">
        <span class="w-full flex my-2" v-if="numberOrderChanged">
          <p class="grow text-red-700">
            Your sort order changes are unsaved, when done sorting, please hit the 'Save
            Changes' button to save
          </p>
          <SaveButton @click="saveSortedNumbers" />
        </span>
        <AddButton @click="addClick(true)">Add Number</AddButton>
        <div
          v-for="numberItem in numbers"
          :key="numberItem.label"
          class="my-2 bg-gray-100 border-t flex"
        >
          <div class="flex-col mx-2 py-2">
            <i-heroicons-outline-chevron-up
              class="h-5 w-5 mt-1 mb-2"
              :class="
                numberItem.sortOrder == Math.min(...numbers.map((d) => d.sortOrder))
                  ? 'text-gray-400'
                  : 'cursor-pointer hover:bg-blue-200'
              "
              @click="swapNumberSort(numberItem.sortOrder, true)"
            />
            <i-heroicons-outline-chevron-down
              class="h-5 w-5"
              :class="
                numberItem.sortOrder == Math.max(...numbers.map((d) => d.sortOrder))
                  ? 'text-gray-400'
                  : 'cursor-pointer hover:bg-blue-200'
              "
              @click="swapNumberSort(numberItem.sortOrder, false)"
            />
          </div>
          <div
            class="flex-col cursor-pointer hover:bg-blue-200 grow px-4 py-2"
            @click="editClick(numberItem)"
          >
            <div>{{ numberItem.label }}</div>
            <div v-if="numberItem.subLabel" class="ml-1">{{ numberItem.subLabel }}</div>
            <div class="ml-2 font-bold text-gray-700">
              {{ numberItem.emergencyNumber }}
            </div>
          </div>
        </div>
      </div>
      <div v-else class="mt-8">
        <span>-- No Numbers found for this building --</span>
      </div>
      <div>
        <AddButton @click="addClick(false)">Add Number</AddButton>
      </div>
      <EditPopup
        entity-name="Number"
        :is-visible="isPopupVisible"
        :is-edit-mode="isEditing"
        :save-error="saveError"
        :delete-enabled="true"
        @close="isPopupVisible = false"
        @save="saveClick"
        @delete="deleteClick"
      >
        <BaseInput
          label="Label"
          v-model="numberLabel"
          placeholder="(enter descriptive name for this number)"
          :error="errors.numberLabel"
        />
        <BaseInput
          label="SubLabel"
          v-model="numberSubLabel"
          placeholder="(enter any extra info for this number)"
          :error="errors.numberSubLabel"
        />
        <BaseInput
          label="Emergency Number"
          v-model="numberValue"
          placeholder="(enter number)"
          :error="errors.numberValue"
        />
        <p class="mt-8 mb-1">Edit Visibility</p>
        <BaseCheckbox
          :model-value="numberAllowAnon"
          label="Anonymous"
          @update:modelValue="numberAllowAnon = $event"
        />
        <BaseCheckboxGroup
          :model-value="numberSelectedRoleOptions"
          :options="numberRoleOptions"
          label=""
          vertical
        />
      </EditPopup>
    </div>
    <div v-else>You need permission to view this page.</div>
  </AdminLayout>
</template>

<script setup lang="ts">
import { useBldgStore } from "../../store/bldgStore";
import { NumberDTO, PermissionType } from "../../store/generatedFetchClient";

import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { useUserStore } from "../../store/userStore";
import { NameValue } from "../../sharedTypes";

const bldgStore = useBldgStore();

var isPropertyLoading = computed(() => bldgStore.isLoading);
var property = computed(() => bldgStore.property);
var numbers = ref(null as NumberDTO[]);

var userHasPermission = computed(() =>
  useUserStore().hasPermission(PermissionType.EditContacts)
);

onMounted(loadData);
watch(property, loadData);

function loadData() {
  if (property.value?.siteRoles) {
    numberRoleOptions.value = [];
    property.value.siteRoles.forEach((r) =>
      numberRoleOptions.value.push({ name: r.roleAlias, value: r.siteRoleId })
    );
  }

  //Assign fresh sortOrder values based off the current order (in case of unmigrated)
  let newSortValue = 0;
  numbers.value = bldgStore.property.numbers.map((number) => {
    return {
      ...number,
      sortOrder: newSortValue++,
    };
  });
}

// Popup stuff
let isPopupVisible = ref(false);
let isEditing = ref(false);
let saveError = ref("");

const { handleSubmit, errors } = useForm({
  validationSchema: yup.object({
    numberLabel: yup.string().required().max(255).label("Label"),
    numberSubLabel: yup.string().max(255).label("SubLabel"),
    numberValue: yup.string().required().max(50).label("Emergency Number"),
  }),
  initialValues: {
    numberLabel: "",
    numberSubLabel: "",
    numberValue: "",
  },
});

let numberId = ref(-1);
const { value: numberLabel } = useField<string>("numberLabel");
const { value: numberSubLabel } = useField<string>("numberSubLabel");
const { value: numberValue } = useField<string>("numberValue");
let numberAllowAnon = ref(false);
let numberRoleOptions = ref([] as NameValue[]);
let numberSelectedRoleOptions = ref([] as number[]);
let numberSortOrder = ref(-1);

let saveClick = handleSubmit(onValidSubmit, onInvalidSubmit);

async function onValidSubmit() {
  let saveNumber = {
    numberId: numberId.value,
    label: numberLabel.value,
    subLabel: numberSubLabel.value,
    emergencyNumber: numberValue.value,
    allowAnonView: numberAllowAnon.value,
    siteRoleIDs: numberSelectedRoleOptions.value,
    sortOrder: numberSortOrder.value,
  } as NumberDTO;

  saveError.value = "";

  if (property.value && property.value.portalId) {
    let possibleError = await bldgStore.savePropertyNumber(
      property.value?.portalId,
      saveNumber
    );
    if (!possibleError) {
      isPopupVisible.value = false;
    } else {
      //handle error
      saveError.value = possibleError;
    }
  }
}

function onInvalidSubmit(ctx: any) {
  saveError.value = "One or more invalid form inputs.";
  console.log("error trying to submit form", ctx);
}

let addClick = function (top: Boolean) {
  numberId.value = 0;
  numberLabel.value = "";
  numberSubLabel.value = "";
  numberValue.value = "";
  saveError.value = "";
  numberAllowAnon.value = true;
  numberSelectedRoleOptions.value = numberRoleOptions.value.map((o) => o.value as number);

  if (top) {
    numberSortOrder.value = Math.min(...numbers.value.map((d) => d.sortOrder)) - 1;
  } else {
    numberSortOrder.value = Math.max(...numbers.value.map((d) => d.sortOrder)) + 1;
  }

  isEditing.value = false;
  isPopupVisible.value = true;
};

let editClick = function (selectedNumber: NumberDTO) {
  numberId.value = selectedNumber.numberId ?? 0;
  numberLabel.value = selectedNumber.label ?? "";
  numberSubLabel.value = selectedNumber.subLabel ?? "";
  numberValue.value = selectedNumber.emergencyNumber ?? "";
  numberAllowAnon.value = selectedNumber.allowAnonView;
  numberSelectedRoleOptions.value = selectedNumber.siteRoles.map((r) => r.siteRoleId);
  numberSortOrder.value = selectedNumber.sortOrder;

  saveError.value = "";

  isEditing.value = true;
  isPopupVisible.value = true;
};

async function deleteClick() {
  if (isEditing.value && numberId.value > 0) {
    if (await bldgStore.deletePropertyNumber(numberId.value)) {
      isPopupVisible.value = false;
    }
  }
}

var numberOrderChanged = ref(false);

function swapNumberSort(sortID: number, sortUp: boolean) {
  let selectedNumberID = numbers.value.find((number) => number.sortOrder == sortID)
    .numberId;

  if (sortUp) {
    let swapNumberID = numbers.value.find((number) => number.sortOrder == sortID - 1)
      .numberId;
    numbers.value.find((number) => number.numberId == selectedNumberID).sortOrder =
      sortID - 1;
    numbers.value.find((number) => number.numberId == swapNumberID).sortOrder = sortID;

    numberOrderChanged.value = true;
  } else {
    let swapNumberID = numbers.value.find((number) => number.sortOrder == sortID + 1)
      .numberId;
    numbers.value.find((number) => number.numberId == selectedNumberID).sortOrder =
      sortID + 1;
    numbers.value.find((number) => number.numberId == swapNumberID).sortOrder = sortID;

    numberOrderChanged.value = true;
  }

  sortAllNumbers();
}

function sortAllNumbers() {
  numbers.value.sort((x, y) => {
    if (x.sortOrder < y.sortOrder) {
      return -1;
    } else if (x.sortOrder > y.sortOrder) {
      return 1;
    }
    return 0;
  });
}

async function saveSortedNumbers() {
  let orderToSave = {
    numberOrders: numbers.value.map((number) => {
      return {
        numberId: number.numberId,
        sortOrder: number.sortOrder,
      };
    }),
  };

  saveError.value = await bldgStore.saveNumberOrder(orderToSave);

  if (saveError.value == null) numberOrderChanged.value = false;

  loadData();
}
</script>
